/**
 * `core.json` — the typed accessor over the OEM+ **eager seed**.
 *
 * `core.json` is the one OEM+ data file imported directly into the bundle; it
 * is available on first paint with no fetch. Everything else arrives lazily
 * through `useOemData()`. Its twelve keys are the demo's `B` keys that every
 * surface needs: the fleet itself, the site register, the model catalogue, the
 * per-component condition register, and the rate/energy/cost tables the
 * derivations multiply against.
 *
 * This module is the **only** place that touches the raw JSON. It casts once,
 * names everything the way the demo named it, and hands out `readonly` typed
 * constants. Nothing downstream should `import … from './core.json'`.
 *
 * ## Ported from `OEM+_Platform_Demo.html`
 *
 * Source line 1840, in full:
 *
 * ```js
 * const FLEET = B.fleet, BYID = Object.fromEntries(FLEET.map(f=>[f.machine_id,f])),
 *       MINES = B.mines, IDS = B.ids, MINE_ORDER = ['KG','BR','MI','OH','GD','MR'];
 * ```
 *
 * Source line 1839 supplies `HEALTH = unpack(B.health)`.
 *
 * ## Layering
 *
 * Data Schema layer (layer 1). Pure module-scope derivation: no React, no
 * engine state, no clock, no randomness. The two derived constants
 * ({@link BY_ID}, {@link HEALTH}) are computed **once** at module load —
 * ~1,168 objects, well under a millisecond — so no surface pays for them twice.
 *
 * @see scripts/oem/README.md — key placement and byte budget
 * @see src/versions/v1/schema/machine.ts — `Machine`, `MachineClassDef`, bands
 * @see src/versions/v1/shared/useOemData.ts — everything that is *not* here
 * @see docs/foundations/4-layer-architecture.md
 */

import type {
  ConditionBand,
  Machine,
  MachineClass,
  MachineClassDef,
  MineCode,
  ModelCode,
} from '../schema/machine.ts';
import { unpackTable, type ColumnarTable } from '../shared/columnar.ts';
import coreJson from './core.json';

// ---------------------------------------------------------------------------
// Types not yet in schema/
// ---------------------------------------------------------------------------
//
// Each interface below is a minimal, faithful shape for one `core.json` key.
// They live here only until the schema lane lands the matching file; the TODO
// on each names its destination. Field docs stay with the type when it moves.

/**
 * One row of the per-component condition register — `core.json.health`,
 * hydrated. 1,068 rows: every machine's components, one row each.
 *
 * `status` is the band the score already falls in; it is stored, not derived.
 * It agrees with `bandOf(health_score).key` for every row in the dataset — but
 * derive with `bandOf()` rather than trusting the field if the two ever
 * disagree, since `bandOf()` is the port's single source of truth for
 * thresholds.
 *
 * TODO: move to schema/health.ts once that lane lands.
 */
export interface ComponentHealth {
  /** Machine this component belongs to — joins `Machine.machine_id`. */
  machine_id: string;
  /** Component name, from the owning model's `MachineClassDef.components`. */
  component: string;
  /** Condition score, 0–100. */
  health_score: number;
  /** Stored band for {@link ComponentHealth.health_score}. */
  status: ConditionBand;
  /** Operating hours since the component was last changed out. */
  hours_since_change: number;
  /** Reliability-engineer note, or `null` where none was recorded. */
  note: string | null;
}

/**
 * A mine site — a value of `core.json.mines`, keyed by {@link MineCode}.
 * The richest record in the eager core: identity, geology, commercial rates,
 * named contacts and the per-class lost-production cost the engine prices
 * downtime with.
 *
 * TODO: move to schema/mine.ts once that lane lands.
 */
export interface Mine {
  /** Site code; equals its key in `MINES`. */
  id: MineCode;
  /** Display name, e.g. `'Kalbarri Gold'`. */
  name: string;
  /** Australian state/territory abbreviation, e.g. `'WA'`. */
  state: string;
  /** Long-form region, e.g. `'Eastern Goldfields, Western Australia'`. */
  region: string;
  commodity: string;
  depth_m: number;
  /** `'Underground'` | `'Open pit'` | `'Open pit + underground'`. */
  mining: string;
  /** Mining method, free text. */
  method: string;
  ownership: string;
  /** Ground-conditions narrative — drives the "why this site is hard" copy. */
  ground: string;
  operator_model: string;
  /** Qualitative operator assessment, free text starting `'Good'`/`'Fair'`/`'Poor'`. */
  operator_quality: string;
  /** `'Live'` (telematics) or `'Shift-end'` (batch export). */
  data_mode: string;
  /** Fleet management system in use. */
  fms: string;
  /** Distribution centre serving the site. */
  dc: string;
  /** Road freight transit time from {@link Mine.dc}, in days. */
  freight_days: number;
  /** Site labour rate, AUD per hour. */
  labour_rate: number;
  /** Crew names used by work orders and the cost-by-crew rollup. */
  crews: string[];
  maint_super: string;
  planner: string;
  /** Reliability engineer, or `'—'` where the site has none. */
  reliability: string;
  /**
   * Cost of one hour of lost production, AUD, by {@link MachineClass}.
   * Sparse — a site only carries entries for the classes it runs (Ochre Hill is
   * surface drills only, so it has `sdrill` alone).
   */
  lost_prod: Partial<Record<MachineClass, number>>;
  /** IANA timezone, e.g. `'Australia/Perth'`. */
  tz: string;
  seismic: boolean;
  lat: number;
  lon: number;
  /** Grid emissions intensity, kg CO2e per kWh. */
  grid_kgco2_kwh: number;
  /** Diesel price, AUD per litre. */
  diesel_aud_l: number;
  /** Electricity price, AUD per kWh. */
  power_aud_kwh: number;
  power_source: string;
}

/**
 * The map-pin subset of a {@link Mine} — a value of `core.json.mines_geo`.
 * Kept separate from `mines` in the source so map surfaces can render without
 * pulling the full site record.
 *
 * TODO: move to schema/mine.ts once that lane lands.
 */
export interface MineGeo {
  lat: number;
  lon: number;
  name: string;
  state: string;
}

/**
 * The commercial rate card for one site — a value of `core.json.rates`.
 *
 * A deliberate denormalisation in the source: every field here also appears on
 * {@link Mine} under a different name (`labour` ↔ `labour_rate`, `lost` ↔
 * `lost_prod`). The demo's costing functions read `RATES`; the site pages read
 * `MINES`. Both are kept so ported code matches the original line for line.
 *
 * TODO: move to schema/mine.ts once that lane lands.
 */
export interface SiteRates {
  /** Distribution centre — mirrors {@link Mine.dc}. */
  dc: string;
  /** Freight transit days — mirrors {@link Mine.freight_days}. */
  freight_days: number;
  /** Labour rate, AUD per hour — mirrors {@link Mine.labour_rate}. */
  labour: number;
  /** Lost production, AUD per hour by class — mirrors {@link Mine.lost_prod}. */
  lost: Partial<Record<MachineClass, number>>;
}

/**
 * One site's energy prices and emissions factors — a row of
 * `core.json.energy_factors`, one per site (6 rows).
 *
 * TODO: move to schema/energy.ts once that lane lands.
 */
export interface EnergyFactors {
  mine: MineCode;
  /** Diesel price, AUD per litre. */
  diesel_aud_per_l: number;
  /** Diesel combustion factor, kg CO2e per litre — 2.68 at every site. */
  diesel_kgco2_per_l: number;
  /** Electricity price, AUD per kWh. */
  power_aud_per_kwh: number;
  /** Grid emissions intensity, kg CO2e per kWh. */
  grid_kgco2_per_kwh: number;
  /** Generation mix description, e.g. `'Grid (TAS, hydro-dominant)'`. */
  power_source: string;
  /** Provenance line shown under the figures. */
  source: string;
}

/**
 * 18-month direct maintenance cost for one machine — a row of
 * `core.json.cost_per_machine` (100 rows, one per machine).
 *
 * TODO: move to schema/cost.ts once that lane lands.
 */
export interface MachineCost {
  machine_id: string;
  /** Total direct maintenance spend over the 18-month window, AUD. */
  amount_aud: number;
  /** Operating hours over the same window. */
  op_hours_18m: number;
  /** {@link MachineCost.amount_aud} ÷ {@link MachineCost.op_hours_18m}, AUD. */
  cost_per_op_hour_aud: number;
}

// ---------------------------------------------------------------------------
// The raw import
// ---------------------------------------------------------------------------

/**
 * The shape of `core.json` as this module reads it.
 *
 * `resolveJsonModule` gives the import a structural type inferred from the
 * literal — 118 KB of it, including a 1,068 × 6 array of value unions. Asking
 * TypeScript to reconcile that against the hand-written shapes above is slow
 * and produces unreadable errors, so the import is cast through `unknown` in
 * one place and every consumer below reads a real type.
 *
 * The cast is the trust boundary. `scripts/oem/extract-bundle.mjs` is what
 * actually guarantees the shape; a mismatch shows up there, not here.
 */
interface CoreJson {
  classes: Record<ModelCode, MachineClassDef>;
  contractors: Partial<Record<MineCode, string>>;
  cost_per_machine: MachineCost[];
  energy_factors: EnergyFactors[];
  fleet: Machine[];
  health: ColumnarTable<ComponentHealth>;
  ids: Record<string, string>;
  mines: Record<MineCode, Mine>;
  mines_geo: Record<MineCode, MineGeo>;
  platform: string;
  rates: Record<MineCode, SiteRates>;
  workings: Partial<Record<MineCode, string[]>>;
}

const core = coreJson as unknown as CoreJson;

// ---------------------------------------------------------------------------
// Fleet
// ---------------------------------------------------------------------------

/**
 * The 100-machine fleet, in source order — the demo's `FLEET` (`B.fleet`,
 * source line 1840).
 *
 * Source order is machine-id order within class (`J-01`…`J-13`, `L-01`…, …)
 * and is what every unsorted list renders. Sort explicitly when a surface wants
 * a different order; never sort this array in place.
 */
export const FLEET: readonly Machine[] = core.fleet;

/**
 * Machine id → machine — the demo's `BYID` (source line 1840). Built once at
 * module load.
 *
 * Indexing returns `Machine` for any string, so a bad id gives `undefined` at
 * runtime with no compile-time warning. That matches the demo and keeps the
 * hundreds of `BYID[row.machine_id]` call sites clean; where an id comes from
 * user input rather than from the data, check with {@link hasMachine} first.
 */
export const BY_ID: Readonly<Record<string, Machine>> = Object.fromEntries(
  FLEET.map((m) => [m.machine_id, m]),
);

/** True when `machineId` names a machine in {@link FLEET}. */
export const hasMachine = (machineId: string): boolean =>
  Object.prototype.hasOwnProperty.call(BY_ID, machineId);

// ---------------------------------------------------------------------------
// Sites
// ---------------------------------------------------------------------------

/**
 * The six mine sites, keyed by code — the demo's `MINES` (`B.mines`, source
 * line 1840).
 */
export const MINES: Readonly<Record<MineCode, Mine>> = core.mines;

/** Map-pin subset of {@link MINES} — `B.mines_geo`. */
export const MINES_GEO: Readonly<Record<MineCode, MineGeo>> = core.mines_geo;

/**
 * The demo's fixed site order — source line 1840, verbatim.
 *
 * Not alphabetical and not by size: it is the order the demo presents sites in
 * everywhere (tabs, legends, grouped tables, stacked bars). Iterate this rather
 * than `Object.keys(MINES)` so every surface agrees, and so the ordering does
 * not depend on JSON key order.
 */
export const MINE_ORDER = ['KG', 'BR', 'MI', 'OH', 'GD', 'MR'] as const satisfies readonly MineCode[];

/**
 * Named workings within a site — `B.workings`. **Sparse**: only Gawler Deep
 * (`GD`) and Mount Read Metals (`MR`) are subdivided. Every other site is
 * absent, and a machine there carries `working: null`.
 */
export const WORKINGS: Readonly<Partial<Record<MineCode, string[]>>> = core.workings;

/**
 * Contracting company by site — `B.contractors`. **Sparse**: only the three
 * contractor-involved sites (`MI`, `GD`, `MR`) have an entry; the owner-operated
 * sites are absent.
 */
export const CONTRACTORS: Readonly<Partial<Record<MineCode, string>>> = core.contractors;

/**
 * Commercial rate card by site — `B.rates`. See {@link SiteRates} for the
 * deliberate overlap with {@link MINES}.
 */
export const RATES: Readonly<Record<MineCode, SiteRates>> = core.rates;

/**
 * Energy prices and emissions factors, one row per site — `B.energy_factors`.
 * An array, not a map: the demo renders it as a table in site order.
 */
export const ENERGY_FACTORS: readonly EnergyFactors[] = core.energy_factors;

// ---------------------------------------------------------------------------
// Models, condition, cost
// ---------------------------------------------------------------------------

/**
 * The eleven model definitions, keyed by {@link ModelCode} — `B.classes`.
 * Carries the per-model component list the condition register is built from,
 * plus nominal diesel/electrical consumption and list price.
 */
export const CLASSES: Readonly<Record<ModelCode, MachineClassDef>> = core.classes;

/**
 * Short id → machine id — the demo's `IDS` (`B.ids`, source line 1840).
 *
 * The RMS, telemetry and drill-plan feeds identify machines by a vendor short
 * code (`'L14'`, `'S04'`) that does **not** match the fleet id (`'L-02'`,
 * `'S-04'`). Eleven machines are aliased; the rest appear in the feeds under
 * their fleet id. Resolve inbound feed ids through this map before joining.
 */
export const IDS: Readonly<Record<string, string>> = core.ids;

/**
 * The condition register: every machine's components, 1,068 rows —
 * `unpack(B.health)`, the demo's `HEALTH` (source line 1839).
 *
 * The only columnar table in the eager core, and the only one unpacked at
 * module scope. Doing it here means the ~1,068 allocations happen once at
 * import rather than per render.
 */
export const HEALTH: readonly ComponentHealth[] = unpackTable(core.health);

/**
 * 18-month direct maintenance cost, one row per machine — `B.cost_per_machine`.
 */
export const COST_PER_MACHINE: readonly MachineCost[] = core.cost_per_machine;

/**
 * The platform's display name — `B.platform`, `'AiRis ProjeX OEM+'`. Typed
 * `string` rather than a literal: it is branding copy, not a discriminant.
 */
export const PLATFORM: string = core.platform;
